import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { CSVLink } from 'react-csv'
import { PDFDownloadLink } from '@react-pdf/renderer'
import api from 'app/AxiosInstance'
import { baseUrl } from 'app/Utils/Config'
import { SnackbarContext } from '../../SnackbarProvider'
import LedgerPdf from './LedgerPdf'
import bracketsLogo from 'app/Assets/brackets_logo_colorful.svg'
import cross from 'app/Assets/cross.svg'

import {
  LABEL_SPAN,
  LEDGER_COLUMNS,
  PAYMENT_MODE_LABELS,
  TYPE_LABELS,
  BRAND,
  cellSx,
  cellValue,
  currentMonthRange,
  DATE_PRESET,
  DATE_PRESET_LABELS,
  rangeForPreset,
  fmt,
  isFundAddition,
  fundAdditionRowSx,
} from './ledgerConfig'
import useAccounts from 'app/Components/Accounts/useAccounts'

//* CSV: labels for text columns, raw numbers for amounts (spreadsheet-friendly)
const CSV_HEADERS = LEDGER_COLUMNS.map((c) => ({ label: c.label, key: c.key }))
const toCsvRow = (r) =>
  Object.fromEntries(
    LEDGER_COLUMNS.map((c) => [
      c.key,
      c.numeric ? r?.[c.key] : cellValue(c, r),
    ]),
  )

const getInitialFilters = () => ({
  ...currentMonthRange(),
  fundsId: ``,
  type: `ALL`,
  paymentMode: ``,
  account: ``,
})

//* CEO-only ledger: pick filters -> preview -> download CSV / PDF
const FundsLedger = ({ open, onClose, funds = [] }) => {
  const openSnackbar = useContext(SnackbarContext)
  const token = localStorage.getItem(`AuthToken`)

  const [filters, setFilters] = useState(getInitialFilters)
  const [datePreset, setDatePreset] = useState(DATE_PRESET.CUSTOM)
  const [ledger, setLedger] = useState(null)
  const [loading, setLoading] = useState(false)
  const [logo, setLogo] = useState(undefined) //* undefined = still loading, null = failed
  const { accounts } = useAccounts()

  //* read the existing SVG logo and pass its shapes to the PDF (drawn as vector, no image embedding)
  useEffect(() => {
    fetch(bracketsLogo)
      .then((res) => res.text())
      .then((text) => {
        const svg = new DOMParser().parseFromString(
          text,
          `image/svg+xml`,
        ).documentElement
        setLogo({
          viewBox: svg.getAttribute(`viewBox`),
          paths: [...svg.querySelectorAll(`path`)].map((p) => ({
            d: p.getAttribute(`d`),
            fill: p.getAttribute(`fill`),
            fillRule: p.getAttribute(`fill-rule`),
          })),
        })
      })
      .catch(() => setLogo(null))
  }, [])

  const setFilter = (key) => (e) => {
    setFilters((prev) => ({ ...prev, [key]: e.target.value }))
    setLedger(null) //* filters changed -> preview must be refreshed before export
  }

  //* Last week / last month set from+to; Custom resets to current month and shows pickers
  const handleDatePresetChange = (e) => {
    const preset = e.target.value
    setDatePreset(preset)
    setFilters((prev) => ({ ...prev, ...rangeForPreset(preset, prev) }))
    setLedger(null)
  }

  const fundOptions = useMemo(
    () =>
      funds?.map((f) => ({
        value: f?._id,
        label:
          f?.allocatedTo?.name?.trim() ||
          `Deleted user (${f?.description || f?._id})`,
      })) || [],
    [funds],
  )

  const accountOptions = useMemo(
    () =>
      accounts?.map((a) => ({
        value: a?._id,
        label: a?.name?.trim() || a?._id,
      })) || [],
    [accounts],
  )

  const handlePreview = async () => {
    if (filters.from && filters.to && filters.from > filters.to) {
      openSnackbar(`"From" date must be before "To" date`, `error`)
      return
    }
    try {
      setLoading(true)
      const params = new URLSearchParams(
        Object.entries(filters).filter(([, v]) => v !== ``),
      ).toString()
      const res = await api.get(`${baseUrl}/funds/ledger?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setLedger(res?.data?.data)
    } catch (error) {
      openSnackbar(
        error?.response ? error?.response?.data?.message : error?.message,
        `error`,
      )
    } finally {
      setLoading(false)
    }
  }

  const filtersText = [
    `Range: ${DATE_PRESET_LABELS[datePreset] || datePreset}`,
    `From: ${filters.from || `start`}`,
    `To: ${filters.to || `today`}`,
    `Fund: ${
      fundOptions.find((f) => f.value === filters.fundsId)?.label || `All`
    }`,
    `Type: ${TYPE_LABELS[filters.type] || `All`}`,
    filters.paymentMode
      ? `Mode: ${PAYMENT_MODE_LABELS[filters.paymentMode]}`
      : null,
    filters.account
      ? `Account: ${
          accountOptions.find((a) => a.value === filters.account)?.label ||
          filters.account
        }`
      : null,
  ]
    .filter(Boolean)
    .join(`  |  `)

  const fileName = `funds-ledger-${filters.from || `start`}-to-${
    filters.to || `today`
  }`

  const csvData = ledger
    ? [
        { description: `Opening balance`, balance: ledger?.openingBalance },
        ...(ledger?.rows || []).map(toCsvRow),
        {
          description: `Totals / Closing balance`,
          in: ledger?.totals?.in,
          out: ledger?.totals?.out,
          balance: ledger?.totals?.closingBalance,
        },
      ]
    : []

  //* memoized so the PDF is only rebuilt when the data actually changes
  const pdfDocument = useMemo(
    () => (
      <LedgerPdf
        ledger={ledger}
        filtersText={filtersText}
        logo={logo || undefined}
      />
    ),
    [ledger, logo, filtersText],
  )

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason === `backdropClick`) return  
        onClose()
      }}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: `flex`,
          flexDirection: `row-reverse`,
          justifyContent: `space-between`,
          alignItems: `center`,
        }}
      >
        <Box
          sx={{
            width: `36px`,
            height: `36px`,
            display: `flex`,
            justifyContent: `center`,
            alignItems: `center`,
            padding: `11px`,
            '&:hover': { cursor: `pointer`, opacity: 0.8 },
          }}
          onClick={onClose}
        >
          <img src={cross} alt="close" width="13.87px" height="14px" />
        </Box>
        <Typography component="span" sx={{ fontWeight: 600 }}>
          Funds Ledger
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Date range"
              size="small"
              fullWidth
              value={datePreset}
              onChange={handleDatePresetChange}
            >
              {Object.entries(DATE_PRESET_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {datePreset === DATE_PRESET.CUSTOM && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={filters.from}
                  onChange={setFilter(`from`)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={filters.to}
                  onChange={setFilter(`to`)}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Fund (allocated to)"
              size="small"
              fullWidth
              value={filters.fundsId}
              onChange={setFilter(`fundsId`)}
            >
              <MenuItem value="">All funds</MenuItem>
              {fundOptions.map((f) => (
                <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Type"
              size="small"
              fullWidth
              value={filters.type}
              onChange={setFilter(`type`)}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="IN">{TYPE_LABELS.IN} (allocations)</MenuItem>
              <MenuItem value="OUT">{TYPE_LABELS.OUT} (expenses)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Payment mode"
              size="small"
              fullWidth
              value={filters.paymentMode}
              onChange={setFilter(`paymentMode`)}
            >
              <MenuItem value="">Any</MenuItem>
              {Object.entries(PAYMENT_MODE_LABELS).map(([m, label]) => (
                <MenuItem key={m} value={m}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Accounts type"
              size="small"
              fullWidth
              value={filters.account}
              onChange={setFilter(`account`)}
            >
              <MenuItem value="">Any</MenuItem>
              {accountOptions.map((a) => (
                <MenuItem key={a.value} value={a.value}>
                  {a.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}
            md={1}
            sx={{ display: `flex`, alignItems: `center` }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={handlePreview}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : `Preview`}
            </Button>
          </Grid>
        </Grid>

        {!ledger ? (
          <Typography variant="body2" color="text.secondary">
            Choose filters and click Preview to see the ledger before
            downloading.
          </Typography>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {LEDGER_COLUMNS.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.numeric ? `right` : `left`}
                        sx={cellSx(c)}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={LEDGER_COLUMNS.length - 1}>
                      <b>Opening balance</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{fmt(ledger?.openingBalance)}</b>
                    </TableCell>
                  </TableRow>
                  {ledger?.rows?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={LEDGER_COLUMNS.length} align="center">
                        No entries for these filters
                      </TableCell>
                    </TableRow>
                  )}
                  {(ledger?.rows || []).map((r) => {
                    const addition = isFundAddition(r)
                    return (
                      <TableRow key={r?._id} hover sx={fundAdditionRowSx(r)}>
                        {LEDGER_COLUMNS.map((c) => (
                          <TableCell
                            key={c.key}
                            align={c.numeric ? `right` : `left`}
                            sx={{
                              ...cellSx(c),
                              color:
                                c.key === `in`
                                  ? BRAND.credit
                                  : c.key === `out`
                                  ? `red`
                                  : undefined,
                              fontWeight:
                                addition && c.key === `in` ? 700 : undefined,
                            }}
                          >
                            {cellValue(c, r)}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  })}
                  <TableRow>
                    <TableCell colSpan={LABEL_SPAN}>
                      <b>Totals / Closing balance</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{fmt(ledger?.totals?.in)}</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{fmt(ledger?.totals?.out)}</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{fmt(ledger?.totals?.closingBalance)}</b>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Box sx={{ display: `flex`, gap: 1, mr: `auto`, ml: 1 }}>
          {ledger && (
            <>
              <CSVLink
                data={csvData}
                headers={CSV_HEADERS}
                filename={`${fileName}.csv`}
                style={{ textDecoration: `none` }}
              >
                <Button variant="outlined">Download CSV</Button>
              </CSVLink>
              {logo !== undefined && (
                <PDFDownloadLink
                  document={pdfDocument}
                  fileName={`${fileName}.pdf`}
                  style={{ textDecoration: `none` }}
                >
                  {({ loading: pdfLoading }) => (
                    <Button variant="outlined" disabled={pdfLoading}>
                      {pdfLoading ? `Preparing PDF...` : `Download PDF`}
                    </Button>
                  )}
                </PDFDownloadLink>
              )}
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default FundsLedger
